"use client";

import posthog from "posthog-js";

import { redactAnalyticsText } from "./pageview-redaction";

const MAX_ANALYTICS_LENGTH = 255;

let identifiedPostHogUserId: string | null = null;

export type AnalyticsEventName =
  | "login_attempt"
  | "login_success"
  | "login_failed"
  | "register_started"
  | "register_verified"
  | "organization_create_after_register"
  | "medicine_search_opened"
  | "medicine_search_result_opened"
  | "medicine_analogues_opened"
  | "offers_filter_changed"
  | "offers_sort_changed"
  | "offers_page_changed"
  | "offer_medicine_opened"
  | "price_history_opened"
  | "cart_item_added"
  | "cart_quantity_changed"
  | "cart_item_removed"
  | "cart_refresh_requested"
  | "checkout_opened"
  | "checkout_price_changed"
  | "order_created"
  | "reorder_requested"
  | "sheet_import_selected"
  | "sheet_import_queued"
  | "sheet_import_failed"
  | "language_changed"
  | "region_changed"
  | "dashboard_nav_clicked"
  | "active_usage_heartbeat";

export type AnalyticsPrimitive = string | number | boolean | null;
export type AnalyticsProperties = Record<string, AnalyticsPrimitive | undefined>;

function truncate(value: string): string {
  return value.length > MAX_ANALYTICS_LENGTH ? value.slice(0, MAX_ANALYTICS_LENGTH) : value;
}

export function sanitizeAnalyticsProperties(props: AnalyticsProperties = {}): Record<string, AnalyticsPrimitive> {
  const sanitized: Record<string, AnalyticsPrimitive> = {};

  for (const [rawKey, value] of Object.entries(props)) {
    if (value === undefined) continue;

    const key = truncate(rawKey.trim());
    if (!key) continue;

    if (value === null || typeof value === "number" || typeof value === "boolean") {
      sanitized[key] = value;
      continue;
    }

    if (typeof value === "string") {
      sanitized[key] = truncate(redactAnalyticsText(value.trim()));
    }
  }

  return sanitized;
}

export function trackAnalyticsEvent(name: AnalyticsEventName, props: AnalyticsProperties = {}) {
  const properties = sanitizeAnalyticsProperties(props);
  const userId = typeof properties.user_id === "string" && properties.user_id ? properties.user_id : null;

  if (userId && userId !== identifiedPostHogUserId) {
    posthog.identify(userId, {
      organization_id: properties.organization_id,
      org_role: properties.org_role,
      is_admin: properties.is_admin,
      language: properties.language,
      region: properties.region,
    });
    identifiedPostHogUserId = userId;
  }

  posthog.capture(name, properties);
}

export function resetAnalyticsIdentity() {
  identifiedPostHogUserId = null;
  posthog.reset();
}
